import React, {useCallback, useEffect, useState} from 'react';
import LineupForm from './LineupForm';
import LineupStatus from './LineupStatus';
import LineupDone from './LineupDone';
import {Button, Card, CardContent, Grid, Stack, Typography, Container, useMediaQuery} from '@mui/material';
import WestIcon from '@mui/icons-material/West';
import axios from 'axios';
import {Link} from "react-router-dom";

function Lineup({queue, groupId, setGroupId}) {

    const [group, setGroup] = useState(null);
    const [lineUpDone, setLineUpDone] = useState(false);

    //waiting time for status
    const [waitingTime, setWaitingTime] = useState(null);
    //batches ahead
    const [batchesAhead, setBatchesAhead] = useState(null);
    //whether updating group for status
    const [updateLoading, setUpdateLoading] = useState(false);

    // If queue or queue.endTime does not exist, set queueEndTime to a past date to return true
    const queueEndTime = queue ? new Date(queue.endTime) : new Date(0);
    const endTime12 = queue ? new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: 'true'
    }).format(queueEndTime) : '';

    const isQueuePast = useCallback((queueDate) => {
        if (!queueDate) {
            return true;
        }
        const currentDateTime = new Date();
        return queueDate < currentDateTime;
    }, []);


    const fetchGroup = useCallback(async () => {
        try {
            if (!groupId) return;
            //TO:DO -- temporary fix for group id - remove on production
            if (parseInt(groupId, 10) < 2075) {
                localStorage.removeItem('groupId');
                setGroupId(null);
                return;
            }
            const response = await axios.get(`/api/groups/${groupId}`);

            // Check if response data is valid
            if (!response.data || !response.data.group) {
                // Invalid response, clear localStorage
                localStorage.removeItem('groupId');
                setGroupId(null);
                return;
            }

            // Check if the group belongs to the current active queue
            // If queueId is provided by backend, validate it matches current queue
            if (response.data.queueId !== undefined && queue && queue.queueId !== undefined) {
                if (queue.queueId !== response.data.queueId) {
                    // Group is from a different queue, clear localStorage
                    localStorage.removeItem('groupId');
                    setGroupId(null);
                    return;
                }
            }

            setGroup(response.data.group);
            setUpdateLoading(true);

            let waitingTimeData = parseFloat(response.data.waitingTime);
            setWaitingTime(Math.round(waitingTimeData));

            //set batches ahead
            setBatchesAhead(response.data.batchesAhead);

            //if group finished line-up, set lineUpDone to true
            let groupStatus = response.data?.group?.status;
            if (groupStatus === "INSIDE" || groupStatus === "COMPLETE" || groupStatus === "CANCELLED") {
                setLineUpDone(true);
            } else {
                setLineUpDone(false);
            }

        } catch (error) {
            console.error('Error fetching group:', error);

            //if group does not exist anymore, remove from localstorage
            if (error.response && error.response.status === 404) {
                localStorage.removeItem('groupId');
                setGroupId(null);
            }
        } finally {
            //keep loading symbol on for 1 second
            setTimeout(() => {
                setUpdateLoading(false);
            }, 1000);
        }
    }, [groupId, setGroupId, queue]);

    useEffect(() => {
        if (groupId) {
            localStorage.setItem('groupId', JSON.stringify(groupId));
        }

        //only set interval to run fetchgroup if groupId exists
        let fetchInterval;
        if (groupId) {
            fetchInterval = setInterval(fetchGroup, 5000);
            fetchGroup();
        }

        //clean-up
        return () =>
            clearInterval(fetchInterval); // clean up interval when unmounting
    }, [groupId, fetchGroup]);


    return (
        <Container maxWidth='md'>
            <Grid container alignItems="center" justifyContent="center" sx={{mb: 2}}>
                <Grid item xs={2} sx={{display: 'flex', justifyContent: 'center'}}>
                    <Button aria-label="go back" component={Link} to={'/'} variant="outlined" size="small" color='white'
                            sx={{px: 1, minWidth: 'fit-content'}}>
                        <WestIcon/>
                    </Button>
                </Grid>
                <Grid item xs={8} container justifyContent="center">
                    <Stack>
                        <Typography align="center"
                                    sx={{fontSize: '1.2rem', fontWeight: 700, padding: '10px 5px 0px 5px'}}>
                            Trottier Observatory
                        </Typography>
                        <Typography align="center" sx={{fontSize: '1rem', fontWeight: 500}}>
                            Virtual Line-up
                        </Typography>
                    </Stack>
                </Grid>
                <Grid item xs={2}>
                </Grid>
            </Grid>
            {/* If all group information exists and line up is done, show end page otherwise:
            If groupId exists, show status, else show sign-up form if queue is active.
            If queue is not active and there is no stored groupId, show observatory line-up is closed.*/}
            {
                (lineUpDone && groupId && group) ?
                    (<LineupDone
                        groupId={groupId}
                        setGroupId={setGroupId}
                        group={group}
                        setGroup={setGroup}
                    />)
                    :
                    (groupId ? (<LineupStatus groupId={groupId}
                                              group={group}
                                              setGroup={setGroup}
                                              waitingTime={waitingTime}
                                              batchesAhead={batchesAhead}
                                              updateLoading={updateLoading}
                                              fetchGroup={fetchGroup}/>)
                        : (queue && queue?.isActive && !isQueuePast(queueEndTime) ?
                            (<LineupForm groupId={groupId}
                                         setGroupId={setGroupId}/>)
                            :
                            (<Card elevation={9} rounded="true">
                                <CardContent>
                                    <Typography align="center" sx={{fontSize: "1.5rem", fontWeight: 700, mb: 2}}>
                                        The Trottier Observatory Line-up is closed.
                                    </Typography>
                                </CardContent>
                            </Card>)))
            }
        </Container>
    );
}

export default Lineup;